import React, { useState, useEffect } from 'react';
import api from '../services/api.js';
import Message from '../components/Message.jsx';
import CommonTable from '../components/common/CommonTable.jsx';

const PaymentTerms = () => {
  const [terms, setTerms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ 
    title: '', 
    installments: [{ installmentMonth: '', paymentPercentage: '' }] 
  });
  const [editId, setEditId] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showFilters, setShowFilters] = useState(false);

const [paymentFilters, setPaymentFilters] = useState({
  title: "",
  installment: "",
});

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const data = await api.paymentTerms.getAll();
      setTerms(data);
    } catch (error) {
      console.error('Error fetching payment terms:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate total percentage
    const totalPercentage = formData.installments.reduce((sum, inst) => sum + parseFloat(inst.paymentPercentage || 0), 0);
    if (totalPercentage !== 100) {
      setMessage({ text: `Total payment percentage must be 100%. Current: ${totalPercentage}%`, type: 'error' });
      return;
    }

 // 🔹 Normalize installments safely
const normalizeInstallments = (installments = []) => {
  return installments
    .map(inst => ({
      installmentMonth: Number(inst.installmentMonth),
      paymentPercentage: Number(inst.paymentPercentage)
    }))
    .sort((a, b) => a.installmentMonth - b.installmentMonth);
};

const currentInstallments = normalizeInstallments(formData.installments);

// ✅ Duplicate Title Check
const isDuplicateTitle = terms.some(term =>
  term.title.trim().toLowerCase() === formData.title.trim().toLowerCase() &&
  term.id !== editId
);

if (isDuplicateTitle) {
  setMessage({
    text: 'Payment term with this title already exists',
    type: 'error'
  });
  return;
}

// 🚨 Duplicate Installment Structure Check
const isDuplicateInstallment = terms.some(term => {
  if (term.id === editId) return false;

  let existingInstallments = [];

  if (Array.isArray(term.installments)) {
    existingInstallments = term.installments;
  } else if (typeof term.installments === "string") {
    try {
      existingInstallments = JSON.parse(term.installments);
    } catch {
      existingInstallments = [];
    }
  }

  const normalizedExisting = normalizeInstallments(existingInstallments);

  if (normalizedExisting.length !== currentInstallments.length) {
    return false;
  }

  for (let i = 0; i < normalizedExisting.length; i++) {
    if (
      normalizedExisting[i].installmentMonth !== currentInstallments[i].installmentMonth ||
      normalizedExisting[i].paymentPercentage !== currentInstallments[i].paymentPercentage
    ) {
      return false;
    }
  }

  return true;
});

if (isDuplicateInstallment) {
  setMessage({
    text: 'Payment term with same installment structure already exists!',
    type: 'error'
  });
  return;
}

    try {
      if (editId) {
        await api.paymentTerms.update(editId, formData);
        setMessage({ text: 'Payment term updated successfully!', type: 'success' });
      } else {
        await api.paymentTerms.create(formData);
        setMessage({ text: 'Payment term created successfully!', type: 'success' });
      }
      resetForm();
      fetchTerms();
    } catch (error) {
      console.error('Error saving payment term:', error);
      setMessage({ text: 'Error saving payment term!', type: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({ title: '', installments: [{ installmentMonth: '', paymentPercentage: '' }] });
    setEditId(null);
    setShowModal(false);
  };

  const handleEdit = (term) => {
    let installments = [{ installmentMonth: '', paymentPercentage: '' }];
    
    if (term.installments) {
      if (Array.isArray(term.installments)) {
        installments = term.installments;
      } else if (typeof term.installments === 'string') {
        try {
          installments = JSON.parse(term.installments);
        } catch (e) {
          console.error('Error parsing installments:', e);
        }
      }
    }
    
    setFormData({ 
      title: term.title, 
      installments: installments
    });
    setEditId(term.id);
    setShowModal(true);
  };

const filteredTerms = (terms || []).filter((term) => {

  const matchesTitle =
    !paymentFilters.title ||
    term.title
      .toLowerCase()
      .includes(paymentFilters.title.toLowerCase());

  const matchesInstallment =
    !paymentFilters.installment ||
    (Array.isArray(term.installments) &&
      term.installments.some(
        (inst) =>
          String(inst.installmentMonth) ===
          String(paymentFilters.installment)
      ));

  return matchesTitle && matchesInstallment;
});


  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment term?')) {
      try {
        await api.paymentTerms.delete(id);
        setMessage({ text: 'Payment term deleted successfully!', type: 'success' });
        fetchTerms();
      } catch (error) {
        console.error('Error deleting payment term:', error);
        setMessage({ text: 'Error deleting payment term!', type: 'error' });
      }
    }
  };

  const addInstallment = () => {
    setFormData({
      ...formData,
      installments: [...formData.installments, { installmentMonth: '', paymentPercentage: '' }]
    });
  };

  const removeInstallment = (index) => {
    if (formData.installments.length > 1) {
      const newInstallments = formData.installments.filter((_, i) => i !== index);
      setFormData({ ...formData, installments: newInstallments });
    }
  };

  const updateInstallment = (index, field, value) => {
    const newInstallments = [...formData.installments];
    newInstallments[index][field] = value;
    setFormData({ ...formData, installments: newInstallments });
  };

  const getTotalPercentage = () => {
    return formData.installments.reduce((sum, inst) => sum + parseFloat(inst.paymentPercentage || 0), 0);
  };

  return (
    <div className="payment-terms">
      {message.text && (
        <Message message={message.text} type={message.type} onClose={() => setMessage({ text: '', type: '' })} />
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">

  <h2 className='hd_a'>Payment Terms</h2>

  <div className="d-flex align-items-center gap-2">

    <button
      type="button"
      className={`btn ${
        Object.values(paymentFilters).some(v => v !== "")
          ? "btn-primary"
          : "btn-outline-primary"
      }`}
      onClick={() => setShowFilters(!showFilters)}
    >
      <i className="bi bi-funnel"></i>
    </button>

    <button
      className="btn btn-primary"
      onClick={() => setShowModal(true)}
    >
      <i className="bi bi-plus-lg me-1"></i> Add Payment Term
    </button>

  </div>
</div>
{/* div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className='hd_a'>Payment Terms</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <i className="bi bi-plus-lg me-1"></i> Add Payment Term
        </button>
      </div> */}
      {showFilters && (
  <div className="card shadow-sm mb-3">
    <div className="card-body py-2">

      <div className="row g-3">

        <div className="col-md-4">
          <label className="form-label">Title</label>

          <select
  className="form-control"
  value={paymentFilters.title}
  onChange={(e) =>
    setPaymentFilters({
      ...paymentFilters,
      title: e.target.value,
    })
  }
>
  <option value="">All Titles</option>

  {(terms || []).map((term) => (
    <option key={term.id} value={term.title}>
      {term.title}
    </option>
  ))}
</select>
        </div>

        <div className="col-md-4">
          <label className="form-label">Installment</label>

          <select
            className="form-control"
            value={paymentFilters.installment}
            onChange={(e) =>
              setPaymentFilters({
                ...paymentFilters,
                installment: e.target.value,
              })
            }
          >
            <option value="">All</option>

            {[1,2,3,4,5,6,7,8,9,10,11,12].map((month) => (
              <option key={month} value={month}>
                {month} {month === 1 ? "Month" : "Months"}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-2 d-flex align-items-end">
          <button
            type="button"
            className="btn btn-outline-primary w-100"
            onClick={() =>
              setPaymentFilters({
                title: "",
                installment: "",
              })
            }
          >
            Clear
          </button>
        </div>

      </div>

    </div>
  </div>
)}

      {showModal && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white py-2">
                <h6 className="modal-title mb-0">{editId ? 'Edit' : 'Add'} Payment Term</h6>
                <button type="button" className="btn-close btn-close-white" onClick={resetForm}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-3">
                  <div className="mb-3">
                    <label className="form-label small mb-1">Title *</label>
                    <input type="text" className="form-control form-control-sm" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                  </div>
                  <div>
                    <label className="form-label small mb-2">Installments</label>
                    <div className="border rounded p-2" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {formData.installments.map((inst, index) => (
                        <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                          <div style={{ flex: '1' }}>
                            <select
                              className="form-control form-control-sm" 
                              value={inst.installmentMonth} 
                              onChange={(e) => updateInstallment(index, 'installmentMonth', e.target.value)} 
                              required
                            >
                            
                              <option value="1">1 Month</option>
                              <option value="2">2 Months</option>
                              <option value="3">3 Months</option>
                              <option value="4">4 Months</option>
                              <option value="5">5 Months</option>
                              <option value="6">6 Months</option>
                              <option value="7">7 Months</option>
                              <option value="8">8 Months</option>
                              <option value="9">9 Months</option>
                              <option value="10">10 Months</option>
                              <option value="11">11 Months</option>
                              <option value="12">12 Months</option>
                            </select>
                          </div>
                          <div style={{ flex: '1' }}>
                            <input 
                              type="number" 
                              step="0.01" 
                              min="0" 
                              max="100" 
                              className="form-control form-control-sm" 
                              placeholder="%"
                              value={inst.paymentPercentage} 
                              onChange={(e) => updateInstallment(index, 'paymentPercentage', e.target.value)} 
                              required 
                            />
                          </div>
                          <div className="d-flex gap-1">
                            <button type="button" className="btn btn-sm btn-success px-2 py-1" onClick={addInstallment} title="Add">+</button>
                            {formData.installments.length > 1 && (
                              <button type="button" className="btn btn-sm btn-danger px-2 py-1" onClick={() => removeInstallment(index)} title="Remove">−</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-end mt-2">
                      <small className={getTotalPercentage() === 100 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                        Total: {getTotalPercentage().toFixed(2)}% {getTotalPercentage() === 100 ? '✓' : ''}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer py-2">
                  <button type="button" className="btn btn-sm btn-secondary" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn btn-sm btn-primary">Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body px-0 pt-0 p-6">
          <CommonTable
            dataSource={api.paymentTerms.table}
            queryParams={{ title: paymentFilters.title, installment: paymentFilters.installment }}
            tableClassName="table table-sm table-hover mb-0"
            columns={[
              { key: 'title', label: 'Title', sortable: true, className: 'align-middle' },
              { key: 'installments', label: 'Installments', sortable: false, className: 'align-middle', render: (value, term) => Array.isArray(term.installments) && term.installments.length > 0 ? (
                <div className="d-flex flex-wrap gap-1">
                  {term.installments.map((inst, idx) => <span key={idx} className="badge bg-light text-dark border">{inst.installmentMonth} {inst.installmentMonth == 1 ? 'Month' : 'Months'}: {inst.paymentPercentage}%</span>)}
                </div>
              ) : <span className="text-muted small">No installments</span> }
            ]}
            renderActions={(term) => <>
              <button title="Edit" className="btn btn-sm btn-primary me-1" onClick={() => handleEdit(term)} style={{ fontSize: '13px', padding: '4px 10px' }}><i className="bi bi-pencil-square"></i></button>
              <button title="Delete" className="btn btn-sm btn-danger" onClick={() => handleDelete(term.id)} style={{ fontSize: '13px', padding: '4px 10px' }}><i className="bi bi-trash-fill"></i></button>
            </>}
            emptyMessage="No payment terms found"
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentTerms;
